import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { useEffect, useRef, useState } from "react";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Plus } from "lucide-react";
import { DotsSixVerticalIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { Skeleton } from "@libs/zapi-ui/shadcn";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type TSlidesSidebarProps = {
  slides: TSlide[];
  selectedIndex: number;
  onSelectSlide: (index: number) => void;
  onDeleteSlide: (slideId: string) => void;
  onAddSlide: () => void;
  onReorderSlides: (newOrder: TSlide[]) => void;
  isGenerating: boolean;
};

type TSortableSlideItemProps = {
  slide: TSlide;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
};

function SortableSlideItem({ slide, index, isSelected, onSelect, onDelete }: TSortableSlideItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={cn(
        "group relative w-full pl-1 pr-1 py-2.5 text-left transition-all border-innoveraZinc-100 cursor-pointer",
        "hover:bg-innoveraZinc-100 border-l-2 border-l-transparent",
        index === 0 ? "border-y" : "border-b",
        isSelected && "!bg-innoveraOrange-100 !border-l-innoveraOrange-400",
        isDragging && "shadow-lg bg-white"
      )}
    >
      <div className="flex items-center gap-1">
        <button
          className="touch-none cursor-grab active:cursor-grabbing p-1 rounded transition-colors"
          {...attributes}
          {...listeners}
        >
          <DotsSixVerticalIcon className="min-w-4 min-h-4 text-innoveraZinc-400" />
        </button>
        <Typography variant="body2" className="truncate flex-1">
          {slide.title || <span className="text-innoveraZinc-400 italic">Untitled slide</span>}
        </Typography>
        <AppButton
          size="iconSm"
          appearance="text"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded transition-all flex-shrink-0 bg-transparent"
          title="Delete slide"
        >
          <TrashSimpleIcon size={14} />
        </AppButton>
      </div>
    </div>
  );
}

function DragOverlayItem({ slide }: { slide: TSlide }) {
  return (
    <div className="w-[204px] pl-1 pr-1 py-2.5 bg-white border border-innoveraOrange-400 shadow-xl rounded-md">
      <div className="flex items-center gap-1">
        <div className="p-1">
          <DotsSixVerticalIcon className="min-w-4 min-h-4 text-innoveraZinc-400" />
        </div>
        <Typography variant="body2" className="truncate flex-1">
          {slide.title || <span className="text-innoveraZinc-400 italic">Untitled slide</span>}
        </Typography>
      </div>
    </div>
  );
}

export function SlidesSidebar(props: TSlidesSidebarProps) {
  const { slides, selectedIndex, onSelectSlide, onAddSlide, onDeleteSlide, onReorderSlides, isGenerating } = props;
  const [activeId, setActiveId] = useState<string | null>(null);
  const selectedSlideRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (selectedSlideRef.current && sidebarRef.current) {
      const sidebar = sidebarRef.current;
      const selectedElement = selectedSlideRef.current;

      const sidebarRect = sidebar.getBoundingClientRect();
      const selectedRect = selectedElement.getBoundingClientRect();

      const isVisible = selectedRect.top >= sidebarRect.top && selectedRect.bottom <= sidebarRect.bottom;

      if (!isVisible) {
        selectedElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [selectedIndex]);

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = slides.findIndex((slide) => slide.id === active.id);
    const newIndex = slides.findIndex((slide) => slide.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newSlides = arrayMove(slides, oldIndex, newIndex);
    onReorderSlides(newSlides);

    if (selectedIndex === oldIndex) {
      onSelectSlide(newIndex);
    } else if (oldIndex < selectedIndex && newIndex >= selectedIndex) {
      onSelectSlide(selectedIndex - 1);
    } else if (oldIndex > selectedIndex && newIndex <= selectedIndex) {
      onSelectSlide(selectedIndex + 1);
    }
  }

  const activeSlide = activeId ? slides.find((s) => s.id === activeId) : null;

  return (
    <div ref={sidebarRef} className="w-[220px] min-w-[220px] bg-white border border-innoveraZinc-200 flex flex-col">
      <div className="px-4 py-2 bg-white">
        <AppButton
          color="zinc150"
          onClick={onAddSlide}
          className="w-full"
          disabled={slides.length === 0}
          startAdornment={<Plus size={16} />}
          title="Add slide"
        >
          <Typography variant="label2">Slide</Typography>
        </AppButton>
      </div>
      <div className="flex flex-col overflow-y-auto">
        {isGenerating &&
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="w-[calc(100%-32px)] h-8 mx-auto mb-2" />
          ))}
        {slides.length === 0 && !isGenerating && (
          <Typography variant="body3" color="zinc500" className="px-4">
            No slides yet. Generate a slides outline to get started.
          </Typography>
        )}
        {slides.length > 0 && !isGenerating && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {slides.map((slide, index) => (
                <div key={slide.id} ref={index === selectedIndex ? selectedSlideRef : undefined}>
                  <SortableSlideItem
                    slide={slide}
                    index={index}
                    isSelected={index === selectedIndex}
                    onSelect={() => onSelectSlide(index)}
                    onDelete={() => onDeleteSlide(slide.id)}
                  />
                </div>
              ))}
            </SortableContext>
            <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
              {activeSlide ? <DragOverlayItem slide={activeSlide} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
}
